<template>
    <div class="dashboard-body-futuristic">
        <!-- Main Dashboard Container -->
        <main class="futuristic-container">
            <!-- Sidebar Navigation -->
            <AppSidebar activeItem="upload" />

            <!-- Main Card -->
            <section class="dashboard-card-futuristic dashboard-card-scrollable">
                <div class="upload-pro-wrapper">

                    <!-- Page heading -->
                    <div class="upload-pro-head">
                        <div>
                            <h1 class="upload-pro-title">Upload Bids Documents</h1>
                            <p class="upload-pro-sub">Upload a tender's PDFs to get its AI summary, risks and criteria.</p>
                        </div>
                        <div class="upload-pro-badges">
                            <span class="ai-badge">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
                                </svg>
                                AI Engine Ready
                            </span>
                            <div v-if="myPlan" class="pdf-usage-pill" :class="{ warn: pdfUsagePct >= 90 }" :title="pdfUsageTitle">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                </svg>
                                <template v-if="myPlan.max_pdfs_per_month !== null">
                                    {{ myPlan.pdfs_used_this_month }}/{{ myPlan.max_pdfs_per_month }} PDFs used this month
                                </template>
                                <template v-else>
                                    {{ myPlan.pdfs_used_this_month }} PDFs uploaded this month
                                </template>
                            </div>
                        </div>
                    </div>

                    <!-- Pending tender banner -->
                    <div v-if="pendingTender" class="pending-tender-banner">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                        </svg>
                        <div>
                            <strong>Creating project from bookmark:</strong>
                            <span :title="pendingTender.tender_name">{{ pendingTender.tender_name || 'Bookmarked tender' }}</span>
                        </div>
                        <button type="button" class="banner-cancel" @click="cancelPendingTender"
                            aria-label="Don't link this upload to the bookmarked tender"
                            title="Don't link this upload to the bookmarked tender">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>

                    <!-- Status message -->
                    <transition name="fade-slide">
                        <div v-if="uploadStatus.show" :class="['status-message', 'status-' + uploadStatus.type]"
                            :role="uploadStatus.type === 'error' ? 'alert' : 'status'">
                            <svg v-if="uploadStatus.type === 'success'" width="18" height="18" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                            <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2.5" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            {{ uploadStatus.message }}
                        </div>
                    </transition>

                    <!-- Two-column body -->
                    <div class="upload-pro-grid">

                        <!-- LEFT: info / steps -->
                        <aside class="upload-info-panel">
                            <div class="info-hero-icon" aria-hidden="true">
                                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="17 8 12 3 7 8" />
                                    <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                            </div>
                            <h2 class="info-title">How It Works</h2>
                            <p class="info-desc">
                                The AI reads the tender's PDFs and extracts its Bid Summary, eligibility,
                                risks and acceptance criteria.
                            </p>

                            <ul class="info-steps">
                                <li>
                                    <span class="step-no">1</span>
                                    <div>
                                        <strong>Choose a category</strong>
                                        <span>The tender's sector</span>
                                    </div>
                                </li>
                                <li>
                                    <span class="step-no">2</span>
                                    <div>
                                        <strong>Add the PDFs</strong>
                                        <span>Drag them in or browse; several at once is fine</span>
                                    </div>
                                </li>
                                <li>
                                    <span class="step-no">3</span>
                                    <div>
                                        <strong>Add every file now</strong>
                                        <span>You can't add files to the same bid later</span>
                                    </div>
                                </li>
                                <li>
                                    <span class="step-no">4</span>
                                    <div>
                                        <strong>Upload</strong>
                                        <span>Analysis takes about 10–15 minutes; you'll get a notification</span>
                                    </div>
                                </li>
                            </ul>

                            <div class="info-meta">
                                <div class="meta-pill">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2" aria-hidden="true">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                    </svg>
                                    PDF only
                                </div>
                                <div class="meta-pill">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2" aria-hidden="true">
                                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                    </svg>
                                    Up to 50 MB
                                </div>
                            </div>
                        </aside>

                        <!-- RIGHT: upload panel -->
                        <div class="upload-action-panel">
                            <!-- Category selector -->
                            <div class="field-block">
                                <label class="field-label" for="tenderCategory">Tender Category <span class="req" aria-hidden="true">*</span></label>
                                <div class="select-wrap">
                                    <svg class="select-icon" width="18" height="18" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" stroke-width="2" aria-hidden="true">
                                        <path d="M20 7h-9M14 17H5M17 17a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM7 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
                                    </svg>
                                    <select id="tenderCategory" v-model="pdf_category" class="pro-select" required>
                                        <option value="-1" disabled>Select a category…</option>
                                        <option v-for="category in pdfCategories" :key="category.id"
                                            :value="category.id">
                                            {{ category.name }}
                                        </option>
                                    </select>
                                </div>
                            </div>

                            <!-- Drop zone -->
                            <div class="pro-dropzone" :class="{ 'is-dragging': isDragging }" @click="$refs.fileInput.click()"
                                @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave" @drop.prevent="onDrop">
                                <div class="dz-scanline" v-if="isDragging"></div>
                                <div class="dz-icon" aria-hidden="true">
                                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="17 8 12 3 7 8" />
                                        <line x1="12" y1="3" x2="12" y2="15" />
                                    </svg>
                                </div>
                                <p class="dz-primary">
                                    {{ isDragging ? 'Drop files to add them' : 'Drag & drop your bid PDFs here' }}
                                </p>
                                <p class="dz-secondary">or</p>
                                <button class="dz-browse" type="button" @click.stop="$refs.fileInput.click()">
                                    Browse Files
                                </button>
                                <input type="file" ref="fileInput" @change="handleFileChange($event)" hidden
                                    accept=".pdf" multiple>
                                <p class="dz-warning">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2.2" aria-hidden="true">
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="8" x2="12" y2="12" />
                                        <line x1="12" y1="16" x2="12.01" y2="16" />
                                    </svg>
                                    Add every PDF for this tender now. You can't add files to the same bid later.
                                </p>
                                <p class="dz-hint">PDF only, up to 50 MB each</p>
                            </div>

                            <!-- Selected files -->
                            <div v-if="uploadFiles.length" class="files-block">
                                <div class="files-block-head">
                                    <span>{{ uploadFiles.length }} file{{ uploadFiles.length > 1 ? 's' : '' }} selected</span>
                                    <button class="clear-all" type="button" @click="clearFiles">Clear All</button>
                                </div>
                                <ul class="file-cards">
                                    <li v-for="(f, idx) in uploadFiles" :key="idx" class="file-card-row">
                                        <div class="fc-icon" aria-hidden="true">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" />
                                            </svg>
                                        </div>
                                        <div class="fc-info">
                                            <span class="fc-name">{{ f.name }}</span>
                                            <span class="fc-size">{{ formatSize(f.size) }}</span>
                                        </div>
                                        <button class="fc-remove" type="button" @click="removeFile(idx)"
                                            :aria-label="'Remove ' + f.name" title="Remove">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                                                <line x1="18" y1="6" x2="6" y2="18" />
                                                <line x1="6" y1="6" x2="18" y2="18" />
                                            </svg>
                                        </button>
                                    </li>
                                </ul>
                            </div>

                            <!-- View-only notice for non-admin users -->
                            <div v-if="$isViewOnly()" class="view-only-note" style="padding:12px 16px; border-radius:8px; background:rgba(148,163,184,0.12); color:var(--text-secondary,#64748b); font-size:0.9rem; text-align:center;">
                                You have view-only access. Uploading tenders requires an administrator.
                            </div>

                            <!-- Submit -->
                            <button class="pro-submit admin-only" type="button" @click="saveFile"
                                :disabled="isUploading || !uploadFiles.length || pdf_category === '-1'">
                                <svg v-if="isUploading" class="spin" width="20" height="20" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                </svg>
                                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" stroke-width="2" aria-hidden="true">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="17 8 12 3 7 8" />
                                    <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                                {{ isUploading ? 'Uploading…' : 'Upload & Process' }}
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    </div>
</template>

<script>
import axios from 'axios';
import AppSidebar from './Sidebar.vue';
import { notify } from '@/utils/notify';

export default {
    name: 'HomePage',

    components: {
        AppSidebar
    },

    props: {
        loginUser: Function,
        getAccessToken: Function,
    },

    computed: {
        pdfUsagePct() {
            if (!this.myPlan || this.myPlan.max_pdfs_per_month === null || !this.myPlan.max_pdfs_per_month) return 0;
            return Math.min(100, Math.round((this.myPlan.pdfs_used_this_month / this.myPlan.max_pdfs_per_month) * 100));
        },
        pdfUsageTitle() {
            if (!this.myPlan || this.myPlan.max_pdfs_per_month === null) return '';
            const remaining = Math.max(0, this.myPlan.max_pdfs_per_month - this.myPlan.pdfs_used_this_month);
            return `${remaining} PDF upload${remaining === 1 ? '' : 's'} remaining this month`;
        }
    },

    data() {
        return {
            uploadFiles: [],
            pdfCategories: [],
            uploadStatus: {
                show: false,
                message: '',
                type: 'success' // success or error
            },
            pdf_category: '-1',
            isUploading: false,
            isDragging: false,
            pendingTender: null, // { tender_id, tender_name } from bookmark flow
            myPlan: null
        }
    },

    mounted() {
        this.loadData();
        this.loadCategory();
        this.fetchMyPlan();
        // Check if coming from PENDING tab bookmark upload via query params
        const { tender_id, tender_name } = this.$route.query || {};
        if (tender_id) {
            this.pendingTender = {
                tender_id: tender_id,
                tender_name: tender_name || ''
            };
        }
    },

    methods: {

        fileListingPage() {
            this.$router.push({ name: 'BidListing' });
        },

        cancelPendingTender() {
            this.pendingTender = null;
            // Clear query params so a refresh doesn't bring the banner back
            this.$router.replace({ name: 'Home' });
        },

        onDragOver() {
            this.isDragging = true;
        },

        onDragLeave() {
            this.isDragging = false;
        },

        onDrop(event) {
            this.isDragging = false;
            const files = Array.from(event.dataTransfer.files || []);
            this.addFiles(files);
        },

        handleFileChange(event) {
            const files = Array.from(event.target.files || []);
            this.addFiles(files);
            // Clear the input value so the same file can be selected again if removed
            event.target.value = '';
        },

        addFiles(files) {
            const maxSize = 50 * 1024 * 1024; // 50MB

            files.forEach(file => {
                // Validate extension
                if (!file.name.toLowerCase().endsWith('.pdf')) {
                    this.showNotification(`${file.name} isn't a PDF, so it wasn't added.`, 'error');
                    return;
                }
                // Validate size
                if (file.size > maxSize) {
                    this.showNotification(`${file.name} is larger than 50 MB, so it wasn't added. Compress or split it, then add it again.`, 'error');
                    return;
                }
                // Avoid duplicates by name+size
                const exists = this.uploadFiles.some(f => f.name === file.name && f.size === file.size);
                if (!exists) this.uploadFiles.push(file);
            });
        },

        fetchMyPlan: function () {
            axios.get(this.$API_URL + "/user/my-plan", {
                headers: { 'Authorization': 'Bearer ' + this.getAccessToken() },
                suppressErrorAlert: true
            })
                .then((response) => { this.myPlan = response.data; })
                .catch((error) => { console.error('Failed to fetch plan summary', error); });
        },

        loadData: function () {
            const url = this.$API_URL + "/profile";
            axios.get(url, {
                headers: {
                    'Authorization': 'Bearer ' + this.getAccessToken()
                }
            })
                .then(() => {
                    this.loginUser(true);
                })
                .catch((error) => {
                    console.log(error);
                });
        },


        loadCategory: function () {
            const url = this.$API_URL + "/user/get-pdf-category";
            axios.get(url, {
                headers: {
                    'Authorization': 'Bearer ' + this.getAccessToken()
                }
            })
                .then((response) => {
                    this.pdfCategories = response.data;
                })
                .catch((error) => {
                    console.log(error);
                });
        },



        showNotification(message, type = 'success') {
            this.uploadStatus = {
                show: true,
                message: message,
                type: type
            };

            // Auto hide after 5 seconds
            setTimeout(() => {
                this.uploadStatus.show = false;
            }, 5000);
        },

        removeFile(index) {
            this.uploadFiles.splice(index, 1);
        },

        clearFiles() {
            this.uploadFiles = [];
        },

        formatSize(bytes) {
            if (bytes < 1024) return bytes + ' B';
            if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
            return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
        },

        async saveFile() {
            if (!this.$isAdmin()) return;   // viewers cannot upload tenders
            if (this.pdf_category === '-1') {
                this.showNotification("Choose a tender category first.", 'error');
                return;
            }
            if (!this.uploadFiles.length) {
                this.showNotification("Add at least one PDF first.", 'error');
                return;
            }

            if (this.isUploading) return;
            this.isUploading = true;

            const formData = new FormData();
            formData.append('pdf_category', this.pdf_category);
            this.uploadFiles.forEach((file) => {
                formData.append('uploaded_files', file);
            });
            // Coming from Go on a bookmark: link the new bid to its tender.
            if (this.pendingTender && this.pendingTender.tender_id) {
                formData.append('tender_id', this.pendingTender.tender_id);
            }

            try {
                const url = this.$API_URL + "/user/upload-pdf";
                await axios.post(url, formData, {
                    headers: {
                        'Authorization': 'Bearer ' + this.getAccessToken(),
                        'Content-Type': 'multipart/form-data'
                    }
                });

                // The bid now carries the tender_id, so the bookmark that
                // brought us here (Bids › To Upload) has done its job.
                if (this.pendingTender) {
                    try {
                        await axios.delete(this.$API_URL + `/bookmarks/${this.pendingTender.tender_id}`, {
                            headers: { 'Authorization': 'Bearer ' + this.getAccessToken() }
                        });
                    } catch (e) {
                        console.warn('Bookmark delete failed (non-critical):', e);
                    }
                    this.pendingTender = null;
                }

                // Clear selection and show the new bid under Bids › Open
                this.uploadFiles = [];
                this.fetchMyPlan(); // refresh the "PDFs used this month" counter
                notify.success("Upload complete. The analysis takes about 10–15 minutes — you'll get a notification when it's ready.");
                this.$router.push({ name: 'BidListing', query: { tab: 'NEW' } });

            } catch (error) {
                const data = (error && error.response && error.response.data) || {};
                const serverMsg = typeof data.detail === 'string' ? data.detail : data.message;
                const msg = serverMsg || "Couldn't upload the PDFs. Check your connection and try again.";
                this.showNotification(msg, 'error');
                if (serverMsg == 'Unauthorized') {
                    localStorage.removeItem('user-info');
                    this.$router.push({ name: 'LoginPage' });
                }
                console.error('Error uploading files:', error);
            } finally {
                this.isUploading = false;
            }
        }
    }
}
</script>

<style scoped>
.upload-pro-wrapper {
    width: 100%;
    position: relative;
    z-index: 2;
    font-family: var(--font-sans);
}

/* Pending tender banner */
.pending-tender-banner {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: rgba(0, 113, 197, 0.08);
    border: 1.5px solid rgba(0, 113, 197, 0.25);
    border-radius: 12px;
    padding: 0.85rem 1.1rem;
    margin-bottom: 1.25rem;
    color: #0071C5;
    font-size: 0.88rem;
}

.pending-tender-banner > div {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    align-items: center;
}

.pending-tender-banner strong {
    font-weight: 700;
    white-space: nowrap;
}

.pending-tender-banner span {
    font-weight: 500;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 400px;
}

.banner-cancel {
    background: none;
    border: none;
    color: #64748b;
    cursor: pointer;
    line-height: 0;
    padding: 6px;
    border-radius: 6px;
    flex-shrink: 0;
}

.banner-cancel:hover { color: #b42318; background: #fef3f2; }

/* ===== Heading ===== */
.upload-pro-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.75rem;
}

.upload-pro-title {
    font-family: var(--font-display);   /* Outfit — page title */
    font-size: var(--fs-h1);            /* 36px page title */
    line-height: var(--lh-h1);          /* 44px */
    font-weight: var(--fw-h1);          /* 700 */
    color: var(--text-primary);
    margin: 0;
    letter-spacing: -0.02em;
}

.upload-pro-sub {
    margin: 0.3rem 0 0;
    color: #64748b;
    font-size: 0.95rem;
    font-weight: 500;
}

.upload-pro-badges {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    flex-wrap: wrap;
}

.ai-badge {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    background: rgba(16, 185, 129, 0.1);
    color: #059669;
    font-weight: 700;
    font-size: 0.78rem;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    white-space: nowrap;
}

.pdf-usage-pill {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    background: rgba(0, 113, 197, 0.08);
    color: #0071C5;
    font-weight: 700;
    font-size: 0.78rem;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    white-space: nowrap;
}

.pdf-usage-pill.warn {
    background: #fef3f2;
    color: #b42318;
}

/* ===== Status message ===== */
.status-message {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin-bottom: 1.25rem;
    padding: 0.85rem 1.1rem;
    border-radius: 12px;
    font-size: 0.9rem;
    font-weight: 600;
}

.status-success {
    background: rgba(16, 185, 129, 0.1);
    color: #047857;
    border: 1px solid rgba(16, 185, 129, 0.2);
}

.status-error {
    background: #fef3f2;
    color: #b42318;
    border: 1px solid rgba(239, 68, 68, 0.2);
}

.fade-slide-enter-active,
.fade-slide-leave-active {
    transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

/* ===== Two-column grid ===== */
.upload-pro-grid {
    display: grid;
    grid-template-columns: 0.85fr 1.15fr;
    gap: 1.5rem;
    align-items: stretch;
}

/* ===== LEFT info panel ===== */
.upload-info-panel {
    background: linear-gradient(155deg, #001F3F 0%, #003D7A 55%, #0071C5 120%);
    color: #fff;
    border-radius: 20px;
    padding: 2rem 1.75rem;
    position: relative;
    overflow: hidden;
}

.upload-info-panel::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(255, 255, 255, 0.12) 1px, transparent 1px);
    background-size: 26px 26px;
    opacity: 0.4;
    pointer-events: none;
}

.info-hero-icon {
    width: 60px;
    height: 60px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.1rem;
    position: relative;
    z-index: 2;
}

.info-title {
    font-family: var(--font-display);   /* hero/section title */
    font-size: 1.6rem;                   /* a touch larger for presence */
    line-height: 1.25;
    font-weight: 700;
    /* Panel has a dark gradient background — title MUST be white to be legible */
    color: #ffffff !important;
    letter-spacing: -0.01em;
    margin: 0 0 0.6rem;
    position: relative;
    z-index: 2;
}

.info-desc {
    font-size: 0.95rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.88) !important;
    margin: 0 0 1.75rem;
    position: relative;
    z-index: 2;
}

.info-steps {
    list-style: none;
    margin: 0 0 1.75rem;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    position: relative;
    z-index: 2;
}

.info-steps li {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    text-align: left;
}

.step-no {
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.16);
    border: 1px solid rgba(255, 255, 255, 0.25);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.85rem;
    line-height: 30px;
    text-align: center;
    padding-left: 10px;
    box-sizing: border-box;
}

.info-steps li > div {
    flex: 1;
    min-width: 0;
    text-align: left;
}


.info-steps strong {
    display: block;
    font-size: 0.9rem;
    font-weight: 700;
    text-align: left;
}

.info-steps span {
    display: block;
    font-size: 0.78rem;
    opacity: 0.72;
    margin-top: 0.1rem;
    text-align: left;
}

.info-meta {
    display: flex;
    gap: 0.6rem;
    flex-wrap: wrap;
    position: relative;
    z-index: 2;
}

.meta-pill {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.18);
    padding: 0.4rem 0.85rem;
    border-radius: 20px;
    font-size: 0.78rem;
    font-weight: 600;
}

/* ===== RIGHT action panel ===== */
.upload-action-panel {
    background: #fff;
    border: 1px solid #eef2f7;
    border-radius: 20px;
    padding: 1.75rem;
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.04);
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    min-width: 0;
}

.field-block {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.field-label {
    font-size: 0.82rem;
    font-weight: 700;
    color: #334155;
}

.req { color: #b42318; }

.select-wrap {
    position: relative;
    display: flex;
    align-items: center;
}

.select-icon {
    position: absolute;
    left: 1rem;
    color: #64748b;
    pointer-events: none;
}

.pro-select {
    width: 100%;
    padding: 0.85rem 2.5rem 0.85rem 2.75rem;
    border-radius: 12px;
    border: 1.5px solid #e2e8f0;
    background-color: #f8fafc;
    color: #1e293b;
    font-family: inherit;
    font-size: 0.92rem;
    font-weight: 500;
    outline: none;
    cursor: pointer;
    appearance: none;
    transition: all 0.2s;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.85rem center;
    background-size: 1.1rem;
}

.pro-select:focus {
    background-color: #fff;
    border-color: #0071C5;
    box-shadow: 0 0 0 4px rgba(0, 113, 197, 0.1);
}

/* ===== Drop zone ===== */
.pro-dropzone {
    border: 2px dashed #cbd5e1;
    background: #f8fafc;
    border-radius: 16px;
    padding: 2.25rem 1.5rem;
    text-align: center;
    cursor: pointer;
    transition: all 0.25s;
    position: relative;
    overflow: hidden;
}

.pro-dropzone:hover {
    border-color: #0071C5;
    background: #f0f9ff;
}

.pro-dropzone.is-dragging {
    border-color: #0071C5;
    background: rgba(0, 113, 197, 0.08);
    box-shadow: 0 0 0 4px rgba(0, 113, 197, 0.1) inset;
    transform: scale(1.005);
}

.dz-scanline {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, transparent, #0071C5, transparent);
    box-shadow: 0 0 14px #0071C5;
    animation: dzScan 1.6s linear infinite;
}

@keyframes dzScan {
    0% { top: 0; opacity: 0; }
    15% { opacity: 1; }
    85% { opacity: 1; }
    100% { top: 100%; opacity: 0; }
}

.dz-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 0.9rem;
    border-radius: 50%;
    background: rgba(0, 113, 197, 0.1);
    color: #0071C5;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.25s;
}

.pro-dropzone:hover .dz-icon,
.pro-dropzone.is-dragging .dz-icon {
    transform: translateY(-4px);
}

.dz-primary {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #1e293b;
}

.dz-secondary {
    margin: 0.4rem 0;
    font-size: 0.8rem;
    color: #64748b;
}

.dz-browse {
    background: #fff;
    border: 1.5px solid #0071C5;
    color: #0071C5;
    font-weight: 700;
    font-size: 0.88rem;
    padding: 0.6rem 1.4rem;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s;
    font-family: var(--font-sans);
}

.dz-browse:hover {
    background: #0071C5;
    color: #fff;
}

.dz-warning {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    margin: 0.75rem 0 0;
    font-size: 0.78rem;
    font-weight: 600;
    color: #b45309;
}

.dz-warning svg { flex-shrink: 0; }

.dz-hint {
    margin: 0.3rem 0 0;
    font-size: 0.75rem;
    color: #64748b;
    font-weight: 500;
}


/* ===== Selected files ===== */
.files-block {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
}

.files-block-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.82rem;
    font-weight: 700;
    color: #475569;
}

.clear-all {
    background: none;
    border: none;
    color: #b42318;
    font-weight: 700;
    font-size: 0.78rem;
    cursor: pointer;
    font-family: var(--font-sans);
}

.clear-all:hover { text-decoration: underline; }

.file-cards {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-height: 200px;
    overflow-y: auto;
}

.file-cards::-webkit-scrollbar { width: 5px; }
.file-cards::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }

.file-card-row {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.65rem 0.85rem;
    background: #f8fafc;
    border: 1px solid #eef2f7;
    border-radius: 12px;
    transition: all 0.2s;
    animation: fileIn 0.25s ease;
    min-width: 0;
}

@keyframes fileIn {
    from { opacity: 0; transform: translateX(-6px); }
    to { opacity: 1; transform: translateX(0); }
}

.file-card-row:hover {
    border-color: rgba(0, 113, 197, 0.3);
    background: #f0f9ff;
}

.fc-icon {
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    border-radius: 10px;
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
    display: flex;
    align-items: center;
    justify-content: center;
}

.fc-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.fc-name {
    font-size: 0.85rem;
    font-weight: 600;
    color: #1e293b;
    max-width: 100%;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    line-height: 1.35;
}

.fc-size {
    font-size: 0.72rem;
    color: #64748b;
    font-weight: 500;
}

.fc-remove {
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #64748b;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.fc-remove:hover {
    background: #fef3f2;
    color: #b42318;
}

/* ===== Submit ===== */
.pro-submit {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    border: none;
    background: #0071C5;
    color: #fff;
    padding: 1rem;
    border-radius: 12px;
    font-size: 0.95rem;
    font-weight: 700;
    font-family: var(--font-sans);
    cursor: pointer;
    box-shadow: 0 6px 16px rgba(0, 113, 197, 0.3);
    transition: all 0.2s;
    margin-top: auto;
}

.pro-submit:hover:not(:disabled) {
    background: #005a9e;
    box-shadow: 0 10px 22px rgba(0, 113, 197, 0.4);
}

.pro-submit:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
}

.spin { animation: spin 1s linear infinite; }

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

/* ===== Responsive ===== */
@media (max-width: 920px) {
    .upload-pro-grid { grid-template-columns: 1fr; }
}

@media (prefers-reduced-motion: reduce) {
    .spin,
    .file-card-row { animation: none; }
    .dz-scanline { display: none; }
}
</style>
